/**
 * Entity Source Configuration - Single Source of Truth
 * 
 * This file imports from the shared JSON configuration and adds TypeScript types + helpers.
 * 
 * @see shared/config/entity-source-config.json - The actual data (SOT)
 * 
 * RULES:
 * - NEVER hardcode source tables or field mappings in edge functions
 * - ALWAYS import from this file or its edge function mirror
 * - Use getCascadeConfig() for parallel processing settings
 */

import entityData from '../../supabase/functions/_shared/config/entity-source-config.json';
import type { LocalizationContentType } from '@/constants/localization';
import { ENTITY_QUALITY_THRESHOLDS, type QualityThresholds } from './entityQualityThresholds';

// Re-export the JIT fields from JSON
export const JIT_FIELDS = entityData.jitFields as Record<string, string[]>;

/**
 * Check if a field is a JIT (Just In Time) field for an entity type
 */
export function isJITField(entityType: string, field: string): boolean {
  return JIT_FIELDS[entityType]?.includes(field) ?? false;
}

/**
 * Locale strategy determines how source language is determined
 */
export type LocaleStrategy = 
  | 'en'           // Always seed as 'en' (system content)
  | 'source_field' // Read source language from a field in source table (user content)
  | 'dynamic'      // Logic-based (e.g., topics: global=en, location-based=location's language)
  | 'on_demand';   // Translated when viewed, not pre-seeded

/**
 * Entity source configuration interface
 */
export interface EntitySourceConfig {
  // Source table mapping
  sourceTable: string | null;  // null = on-demand only (publication, comment)
  idColumn: string;
  idType: 'uuid' | 'integer' | 'string';
  filterCondition: string | null;
  filterDescription?: string;  // Human-readable filter description for UI tooltips
  
  // Locale strategy
  localeStrategy: LocaleStrategy;
  
  // Column name containing source language (for 'source_field' strategy)
  sourceLanguageField?: string;
  
  // Source fields → entity_fields JSONB mapping
  entityFieldsMapping: Record<string, string>;
  
  // Source fields → metadata column mapping
  sourceFieldsMapping: {
    name: string;           // → translated_name (EN row is SOT)
    slug: string;           // → translated_slug (EN row is SOT)
    body?: string | null;   // → translated_body (optional, null = on-demand)
    excerpt?: string | null; // → translated_body_short (for publications)
    noIndex?: string;       // → no_index flag
  };
  
  // Database AI agent ID (from ai_agent_prompts table)
  agentId: string;
  
  // Quality thresholds for audit
  qualityThresholds: QualityThresholds;
  
  // Is this entity pre-seeded or on-demand?
  isPreSeeded: boolean;
  
  // Is this entity indexable (needs SEO/Social fields)?
  // Non-indexable entities (faq, comment) only need translation fields
  isIndexable: boolean;
  
  // AI-generated fields (from the 15) that DON'T apply to this entity
  nonApplicableAIFields: string[];
  
  // SQL-seeded fields (from the 11) that DON'T apply to this entity
  // Most entities use all 11, but faq/comment don't have slugs etc.
  nonApplicableSqlFields: string[];
  
  // SQL-seeded fields that ARE tracked in Matrix but NOT AI-generated
  // Used for identity fields (translated_name, translated_slug) that come from source table
  sqlSeededFields?: string[];
  
  // Description for UI
  description: string;
  
  /**
   * Whether automated GitHub workflows should process this entity
   * - true: Uses GitHub Actions workflows for seed/fill/cascade
   * - false: UI-only management with manual regeneration
   * 
   * System entities (location, page, route, faq, email_template, practice_area)
   * are set to false - managed exclusively via Admin UI.
   * 
   * User content entities (profile, publication, topic) remain automated.
   */
  automationEnabled: boolean;
}

// Type assertion: Map JSON entities to typed config with quality thresholds from TS
type JsonEntityConfig = typeof entityData.entities[keyof typeof entityData.entities];

function buildEntityConfig(key: LocalizationContentType, json: JsonEntityConfig): EntitySourceConfig {
  return {
    ...json,
    localeStrategy: json.localeStrategy as LocaleStrategy,
    idType: json.idType as 'uuid' | 'integer' | 'string',
    qualityThresholds: ENTITY_QUALITY_THRESHOLDS[key],
  } as EntitySourceConfig;
}

/**
 * Complete entity source configuration for all 10 entity types
 * Built from shared JSON + quality thresholds from TypeScript
 */
export const ENTITY_SOURCE_CONFIG: Record<LocalizationContentType, EntitySourceConfig> = 
  Object.fromEntries(
    Object.entries(entityData.entities).map(([key, value]) => [
      key,
      buildEntityConfig(key as LocalizationContentType, value as JsonEntityConfig),
    ])
  ) as Record<LocalizationContentType, EntitySourceConfig>;

/**
 * Get source config for an entity type
 */
export function getEntitySourceConfig(entityType: LocalizationContentType): EntitySourceConfig {
  return ENTITY_SOURCE_CONFIG[entityType];
}

/**
 * Get all pre-seeded entity types
 */
export function getPreSeededEntityTypes(): LocalizationContentType[] {
  return (Object.entries(ENTITY_SOURCE_CONFIG) as [LocalizationContentType, EntitySourceConfig][])
    .filter(([_, config]) => config.isPreSeeded)
    .map(([type]) => type);
}

/**
 * Get all on-demand entity types
 */
export function getOnDemandEntityTypes(): LocalizationContentType[] {
  return (Object.entries(ENTITY_SOURCE_CONFIG) as [LocalizationContentType, EntitySourceConfig][])
    .filter(([_, config]) => !config.isPreSeeded)
    .map(([type]) => type);
}

/**
 * Get entity types by locale strategy
 */
export function getEntityTypesByLocaleStrategy(strategy: LocaleStrategy): LocalizationContentType[] {
  return (Object.entries(ENTITY_SOURCE_CONFIG) as [LocalizationContentType, EntitySourceConfig][])
    .filter(([_, config]) => config.localeStrategy === strategy)
    .map(([type]) => type);
}

/**
 * Get all automated entity types (processed by GitHub workflows)
 * Returns: ['profile', 'publication', 'topic']
 */
export function getAutomatedEntityTypes(): LocalizationContentType[] {
  return (Object.entries(ENTITY_SOURCE_CONFIG) as [LocalizationContentType, EntitySourceConfig][])
    .filter(([_, config]) => config.automationEnabled)
    .map(([type]) => type);
}

/**
 * Get all UI-only entity types (managed via Admin UI)
 * Returns: ['location', 'page', 'route', 'faq', 'email_template', 'practice_area', 'comment']
 */
export function getUIOnlyEntityTypes(): LocalizationContentType[] {
  return (Object.entries(ENTITY_SOURCE_CONFIG) as [LocalizationContentType, EntitySourceConfig][])
    .filter(([_, config]) => !config.automationEnabled)
    .map(([type]) => type);
}

/**
 * Check if an entity type is automated (processed by GitHub workflows)
 */
export function isAutomatedEntityType(entityType: LocalizationContentType): boolean {
  return ENTITY_SOURCE_CONFIG[entityType]?.automationEnabled ?? false;
}

/**
 * Build entity_fields JSONB from source record
 */
export function buildEntityFieldsJsonb(
  entityType: LocalizationContentType,
  sourceRecord: Record<string, unknown>
): Record<string, unknown> {
  const config = ENTITY_SOURCE_CONFIG[entityType];
  const entityFields: Record<string, unknown> = {};
  
  for (const [sourceColumn, targetKey] of Object.entries(config.entityFieldsMapping)) {
    if (sourceRecord[sourceColumn] !== undefined) {
      entityFields[targetKey] = sourceRecord[sourceColumn];
    }
  }
  
  return entityFields;
}

/**
 * Get the agent ID for an entity type
 */
export function getEntityAgentId(entityType: LocalizationContentType): string {
  return ENTITY_SOURCE_CONFIG[entityType].agentId;
}

/**
 * Get quality thresholds for an entity type
 */
export function getEntityQualityThresholds(entityType: LocalizationContentType): QualityThresholds {
  return ENTITY_SOURCE_CONFIG[entityType].qualityThresholds;
}

/**
 * Check if an entity type uses a database source table
 */
export function hasSourceTable(entityType: LocalizationContentType): boolean {
  const config = ENTITY_SOURCE_CONFIG[entityType];
  return config.sourceTable !== null && config.sourceTable !== 'ROUTE_DEFINITIONS';
}

/**
 * Get all entity types that have database source tables
 */
export function getEntityTypesWithSourceTables(): LocalizationContentType[] {
  return (Object.entries(ENTITY_SOURCE_CONFIG) as [LocalizationContentType, EntitySourceConfig][])
    .filter(([_, config]) => config.sourceTable !== null && config.sourceTable !== 'ROUTE_DEFINITIONS')
    .map(([type]) => type);
}

// ============================================================================
// INDEXABILITY & FIELD APPLICABILITY - Single Source of Truth
// ============================================================================

/**
 * Check if an entity type is indexable (needs SEO/Social fields)
 * Non-indexable entities only need translation fields
 */
export function isEntityIndexable(entityType: LocalizationContentType): boolean {
  return ENTITY_SOURCE_CONFIG[entityType]?.isIndexable ?? true;
}

/**
 * Get non-applicable SQL fields for an entity type
 * Most entities use all 10 SQL fields, but faq/comment exclude some
 */
export function getEntityNonApplicableSqlFields(entityType: LocalizationContentType): string[] {
  return ENTITY_SOURCE_CONFIG[entityType]?.nonApplicableSqlFields || [];
}

/**
 * Get count of applicable SQL-seeded fields for an entity type
 * Total SQL fields = 1 (only translated_slug is tracked in SQL_SEEDED_FIELDS)
 */
export function getEntitySqlFieldCount(entityType: LocalizationContentType): number {
  const nonApplicable = ENTITY_SOURCE_CONFIG[entityType]?.nonApplicableSqlFields || [];
  // Only translated_slug is tracked now - return 0 if excluded, 1 otherwise
  return nonApplicable.includes('translated_slug') ? 0 : 1;
}

/**
 * Get non-applicable AI fields for an entity type
 * All 10 SQL fields always apply; this returns only AI field exclusions
 */
export function getEntityNonApplicableAIFields(entityType: LocalizationContentType): string[] {
  return ENTITY_SOURCE_CONFIG[entityType]?.nonApplicableAIFields || [];
}

/**
 * Get non-translatable fields for an entity type.
 * 
 * Distinguishes "don't AI-generate" (Smart Fill) from "don't translate" (Translator).
 * For publications: translated_body_short is NOT AI-generated (SQL-seeded from excerpt_en)
 * but IS translatable to non-EN locales.
 * 
 * Falls back to nonApplicableAIFields if nonTranslatableFields is not defined.
 */
export function getEntityNonTranslatableFields(entityType: LocalizationContentType): string[] {
  const config = ENTITY_SOURCE_CONFIG[entityType];
  if (!config) return [];
  const rawConfig = config as unknown as Record<string, unknown>;
  return (rawConfig.nonTranslatableFields as string[] | undefined)
    ?? config.nonApplicableAIFields
    ?? [];
}

/**
 * Get SQL-seeded fields for an entity type
 * These fields are tracked in Matrix but excluded from AI generation
 * Used for identity fields (translated_name, translated_slug) that come from source table
 */
export function getEntitySqlSeededFields(entityType: LocalizationContentType): string[] {
  return ENTITY_SOURCE_CONFIG[entityType]?.sqlSeededFields || [];
}

/**
 * Get all indexable entity types
 */
export function getIndexableEntityTypes(): LocalizationContentType[] {
  return (Object.entries(ENTITY_SOURCE_CONFIG) as [LocalizationContentType, EntitySourceConfig][])
    .filter(([_, config]) => config.isIndexable)
    .map(([type]) => type);
}

/**
 * Get all non-indexable entity types
 */
export function getNonIndexableEntityTypes(): LocalizationContentType[] {
  return (Object.entries(ENTITY_SOURCE_CONFIG) as [LocalizationContentType, EntitySourceConfig][])
    .filter(([_, config]) => !config.isIndexable)
    .map(([type]) => type);
}

/**
 * Get count of applicable AI-generated fields for an entity type
 * Total AI fields = 14 (removed entity_fields - internal admin data)
 */
export function getEntityAIFieldCount(entityType: LocalizationContentType): number {
  const nonApplicable = ENTITY_SOURCE_CONFIG[entityType]?.nonApplicableAIFields || [];
  return 14 - nonApplicable.length;
}

/**
 * Get total field count for entity (1 SQL + applicable AI)
 */
export function getEntityTotalFieldCount(entityType: LocalizationContentType): number {
  return getEntitySqlFieldCount(entityType) + getEntityAIFieldCount(entityType);
}

/**
 * Get all entity types
 */
export function getAllEntityTypes(): LocalizationContentType[] {
  return Object.keys(ENTITY_SOURCE_CONFIG) as LocalizationContentType[];
}
